import React, {
  ChangeEventHandler,
  ClipboardEventHandler,
  Component,
  FocusEventHandler,
  KeyboardEventHandler,
} from 'react';
import NumberFormat, { NumberFormatValues, SyntheticInputEvent } from 'react-number-format';

import { Mask, MaskType } from '../../Input/Input';

interface InputNumberFormatProps {
  id?: string;
  value?: string | number;
  name?: string;
  placeholder?: string;
  autoComplete?: string;
  guide?: string;
  isDisabled?: boolean;
  decimalLimit?: number;
  isInteger?: boolean;
  maxValue?: number;
  isMoney?: boolean;
  isDecimal?: boolean;
  isSSN?: boolean;
  isEIN?: boolean;
  isPhone?: boolean;
  isZipCode?: boolean;
  mask?: Mask | MaskType;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onValueChange?: (values: NumberFormatValues, e: SyntheticInputEvent) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onPaste?: ClipboardEventHandler<HTMLInputElement>;
  onCopy?: ClipboardEventHandler<HTMLInputElement>;
  maxLength?: number;
  type?: string;
  autoFocus?: boolean;
  className?: string;
  'aria-label'?: string;
}

class InputNumberFormat extends Component<InputNumberFormatProps> {
  element?: { focus: () => void };

  static displayName: string;

  static defaultProps: {
    value: string;
    name: null;
    placeholder: string;
    autoComplete: string;
    guide: string;
    isDisabled: boolean;
    decimalLimit: number;
    isInteger: boolean;
    maxValue: null;
    isMoney: boolean;
    isDecimal: boolean;
    isSSN: boolean;
    isEIN: boolean;
    isPhone: boolean;
    isZipCode: boolean;
    mask: null;
    onBlur: () => void;
    onFocus: () => void;
    onValueChange: () => void;
    onChange: () => void;
    onKeyDown: () => void;
    onPaste: () => void;
    onCopy: () => void;
    maxLength: null;
    type: string;
    autoFocus: boolean;
    className: string;
  };

  constructor(props: InputNumberFormatProps) {
    super(props);

    this.focus = this.focus.bind(this);
    this.updateRef = this.updateRef.bind(this);
    this.getMask = this.getMask.bind(this);
    this.isAllowed = this.isAllowed.bind(this);
  }

  componentDidMount() {
    if (this.props.autoFocus && this.element) {
      setTimeout(() => this.focus(), 0);
    }
  }

  getMask() {
    const { mask, isSSN, isEIN, isZipCode, isMoney, isPhone, isInteger, isDecimal, decimalLimit, guide } = this.props;

    // start - Deprecate in v2
    if (isSSN || mask === 'ssn') {
      return { mask: guide, format: '###-##-####' };
    }
    if (isEIN || mask === 'ein') {
      return { mask: guide, format: '##-#######' };
    }
    if (isZipCode || mask === 'zipcode') {
      return { mask: guide, format: '#####' };
    }
    if (isPhone || mask === 'phone') {
      return { mask: guide, format: '(###) ###-####' };
    }
    if (mask && typeof mask === 'object') {
      return {
        prefix: '',
        suffix: '',
        ...mask,
      };
    }
    if (isInteger || mask === 'integer') {
      return {
        prefix: '',
        suffix: '',
        thousandSeparator: ',',
        decimalScale: 0,
        allowNegative: true,
        isNumericString: true,
      };
    }
    if (isMoney || isDecimal || mask === 'money' || mask === 'decimal') {
      return {
        prefix: '',
        suffix: '',
        decimalSeparator: '.',
        thousandSeparator: ',',
        decimalScale: decimalLimit,
        allowNegative: true,
        isNumericString: true,
        fixedDecimalScale: true,
      };
    }
    if (mask === 'percent') {
      return {
        prefix: '',
        suffix: '',
        decimalSeparator: '.',
        thousandSeparator: ',',
        decimalScale: 2,
        allowNegative: false,
        isNumericString: true,
        fixedDecimalScale: true,
      };
    }

    // end - Deprecate in v2
    if (mask) {
      return { mask: guide, format: mask };
    }
    return false;
  }

  updateRef(el) {
    this.element = el;
  }

  focus() {
    this.element?.focus();
  }

  isAllowed({ floatValue }) {
    const { maxValue } = this.props;
    if (maxValue === null || floatValue === undefined) {
      return true;
    }
    // @ts-ignore
    return floatValue <= maxValue;
  }

  render() {
    return (
      <NumberFormat
        getInputRef={this.updateRef}
        id={this.props.id}
        name={this.props.name}
        value={this.props.value}
        className={this.props.className}
        onFocus={this.props.onFocus}
        onChange={this.props.onChange}
        onValueChange={this.props.onValueChange}
        onBlur={this.props.onBlur}
        onKeyDown={this.props.onKeyDown}
        onPaste={this.props.onPaste}
        onCopy={this.props.onCopy}
        placeholder={this.props.placeholder}
        disabled={this.props.isDisabled}
        autoComplete={this.props.autoComplete}
        maxLength={this.props.maxLength}
        // @ts-ignore
        type={this.props.type}
        isAllowed={this.isAllowed}
        aria-required={this.props['aria-required']}
        aria-label={this.props['aria-label']}
        {...this.getMask()}
      />
    );
  }
}

InputNumberFormat.defaultProps = {
  value: '',
  name: null,
  placeholder: '',
  autoComplete: 'off',
  guide: '_',
  isDisabled: false,
  decimalLimit: 2,
  isInteger: false,
  maxValue: null,
  isMoney: false,
  isDecimal: false,
  isSSN: false,
  isEIN: false,
  isPhone: false,
  isZipCode: false,
  mask: null,
  onBlur: () => {},
  onFocus: () => {},
  onValueChange: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onPaste: () => {},
  onCopy: () => {},
  maxLength: null,
  type: 'text',
  autoFocus: false,
  className: '',
};

InputNumberFormat.displayName = 'InputNumberFormat';

export default InputNumberFormat;
